import { Card, CardContent, Typography, Box, IconButton, Tooltip } from "@mui/material";
import { BugReport } from "@mui/icons-material";
import Chart from "react-apexcharts";
import { useParams } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import React, { useMemo } from "react";
import { DashboardWidget, AggregateRequest } from "../../types/dashboard/DashboardConfig";
import { useDashboardAggregate } from "../../hooks/useDashboardAggregate";
import LoadingIndicator from "../../components/common/LoadingIndicator/LoadingIndicator";
import { ApexOptions } from "apexcharts";
import { numberWithCommas } from "../../helpers/numberWithCommas";

interface CustomAggregateWidgetProps {
  widget: DashboardWidget;
}

const DebugButton = ({ onClick }: Readonly<{ onClick: () => void }>) => (
  <Tooltip title="Copy API request to clipboard">
    <IconButton size="small" onClick={onClick} sx={{ position: "absolute", top: 4, right: 4, zIndex: 5, opacity: 0.4, "&:hover": { opacity: 1 } }}>
      <BugReport fontSize="small" />
    </IconButton>
  </Tooltip>
);

const ChartCard = ({ title, onDebug, children }: Readonly<{ title: string; onDebug: () => void; children: React.ReactNode }>) => (
  <Card sx={{ height: "100%", display: "flex", flexDirection: "column", position: "relative" }}>
    <DebugButton onClick={onDebug} />
    <CardContent
      sx={{
        flex: 1,
        display: "flex",
        flexDirection: "column",
        minHeight: 0,
        "&:last-child": { pb: 1 },
      }}
    >
      <Typography variant="h6" color="primary" sx={{ flexShrink: 0 }}>
        {title}
      </Typography>
      <Box sx={{ flex: 1, minHeight: 0 }}>{children}</Box>
    </CardContent>
  </Card>
);

export default function CustomAggregateWidget({ widget }: Readonly<CustomAggregateWidgetProps>) {
  const { customerId } = useParams();
  const theme = useTheme();
  const config = widget.config;

  const aggregateRequest: AggregateRequest | null = useMemo(() => {
    if (!config) return null;
    return {
      aggregationType: config.aggregationType ?? "terms",
      field: config.field ?? "interactionType",
      interval: config.interval,
      size: config.size,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      from: config.filters?.from,
      to: config.filters?.to,
      agentName: config.filters?.agentName,
      direction: config.filters?.direction,
      interactionType: config.filters?.interactionType,
      skill: config.filters?.skill,
      group: config.filters?.group,
      tags: config.filters?.tags,
    };
  }, [config]);

  const { data, isLoading } = useDashboardAggregate(
    customerId,
    aggregateRequest,
    widget.id
  );

  const handleCopyDebug = () => {
    if (!aggregateRequest) return;
    const payload = {
      url: `/v1/customers/${customerId}/dashboard/aggregate`,
      body: aggregateRequest,
    };
    navigator.clipboard.writeText(JSON.stringify(payload, null, 2));
  };

  if (isLoading) {
    return (
      <Card sx={{ height: "100%", position: "relative" }}>
        <DebugButton onClick={handleCopyDebug} />
        <CardContent sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%" }}>
          <LoadingIndicator />
        </CardContent>
      </Card>
    );
  }

  if (!data || data.labels.length === 0) {
    return (
      <Card sx={{ height: "100%", position: "relative" }}>
        <DebugButton onClick={handleCopyDebug} />
        <CardContent>
          <Typography variant="h6" color="primary">
            {widget.title}
          </Typography>
          <Typography color="text.secondary" sx={{ mt: 2 }}>
            No data available
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const chartType = config?.chartType ?? "bar";

  // KPI: show a single large number
  if (chartType === "kpi") {
    return (
      <Card sx={{ height: "100%", position: "relative" }}>
        <DebugButton onClick={handleCopyDebug} />
        <CardContent sx={{ display: "flex", flexDirection: "column", justifyContent: "center", height: "100%" }}>
          <Typography variant="h6" color="primary">
            {widget.title}
          </Typography>
          <Typography variant="h3" color="secondary" sx={{ mt: 1 }}>
            {numberWithCommas(data.total)}
          </Typography>
        </CardContent>
      </Card>
    );
  }

  // Pie chart
  if (chartType === "pie") {
    const options: ApexOptions = {
      chart: { type: "pie", animations: { enabled: false } },
      labels: data.labels,
      colors: theme.palette.customColors
        ? Object.values(theme.palette.customColors)
        : undefined,
      legend: { position: "bottom" },
    };
    return (
      <ChartCard title={widget.title} onDebug={handleCopyDebug}>
        <Chart options={options} series={data.values} type="pie" height="100%" />
      </ChartCard>
    );
  }

  // Line chart
  if (chartType === "line") {
    const options: ApexOptions = {
      chart: { type: "line", toolbar: { show: true }, animations: { enabled: false } },
      xaxis: { categories: data.labels },
      yaxis: {
        labels: { formatter: (v) => numberWithCommas(Math.floor(v)) ?? "0" },
      },
      colors: [theme.palette.customColors?.wDarkBlue],
      stroke: { curve: "smooth", width: 2 },
    };
    return (
      <ChartCard title={widget.title} onDebug={handleCopyDebug}>
        <Chart
          options={options}
          series={[{ name: widget.title, data: data.values }]}
          type="line"
          height="100%"
        />
      </ChartCard>
    );
  }

  // Default: bar chart
  const options: ApexOptions = {
    chart: { type: "bar", toolbar: { show: true }, animations: { enabled: false } },
    xaxis: { categories: data.labels },
    yaxis: {
      labels: { formatter: (v) => numberWithCommas(Math.floor(v)) ?? "0" },
      forceNiceScale: true,
    },
    plotOptions: { bar: { borderRadius: 4, horizontal: false } },
    colors: [theme.palette.customColors?.wDarkBlue],
    dataLabels: { enabled: false },
  };

  return (
    <ChartCard title={widget.title} onDebug={handleCopyDebug}>
      <Chart
        options={options}
        series={[{ name: widget.title, data: data.values }]}
        type="bar"
        height="100%"
      />
    </ChartCard>
  );
}
